import React, { useState, useRef, useEffect } from 'react';
import NavelVenueCard from './NavelVenueCard';

function renderMarkdown(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    let processed = line.replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--text-primary);font-weight:600">$1</strong>');
    processed = processed.replace(/(?<!\w)_(.+?)_(?!\w)/g, '<em>$1</em>');
    if (!processed.trim()) return <br key={i} />;
    return <p key={i} className="navel-body" style={{ marginBottom: 8 }} dangerouslySetInnerHTML={{ __html: processed }} />;
  });
}

function resolveImage(section, images) {
  const key = section.imageKey;
  if (!key || !images.dayImages[key]) return null;
  const dayImg = images.dayImages[key];
  if (Array.isArray(dayImg)) return dayImg[section.imageIndex ?? 0] || null;
  return dayImg;
}

function RouteSelector({ routes, selectedId, onSelect }) {
  return (
    <div className="navel-route-selector">
      <p className="navel-label" style={{ marginBottom: 12 }}>Choose your route</p>
      <div className="navel-route-options">
        {routes.map((route) => (
          <button
            key={route.id}
            className={`navel-route-card${selectedId === route.id ? ' navel-route-card--selected' : ''}`}
            onClick={() => onSelect(route.id)}
            type="button"
          >
            <span className="navel-route-emoji">{route.emoji}</span>
            <span className="navel-route-label">{route.label}</span>
            <span className="navel-route-subtitle">{route.subtitle}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function NavelDayContent({ day, tripConfig, copyToClipboard }) {
  const { alternatives, city, images } = tripConfig;

  const [selectedRouteId, setSelectedRouteId] = useState(
    day.routes && day.routes.length > 0 ? day.routes[0].id : null
  );
  const [fadeKey, setFadeKey] = useState(0);
  const contentRef = useRef(null);

  // Reset selected route when day changes
  useEffect(() => {
    if (day.routes && day.routes.length > 0) {
      setSelectedRouteId(day.routes[0].id);
    } else {
      setSelectedRouteId(null);
    }
  }, [day.id]);

  const handleRouteSelect = (id) => {
    if (id !== selectedRouteId) {
      setSelectedRouteId(id);
      setFadeKey((k) => k + 1);
    }
  };

  // Determine which sections and walkingEstimate to render
  let sections, walkingEstimate;
  if (day.routes && day.routes.length > 0) {
    const activeRoute = day.routes.find((r) => r.id === selectedRouteId) || day.routes[0];
    sections = activeRoute.sections;
    walkingEstimate = activeRoute.walkingEstimate;
  } else {
    sections = day.sections;
    walkingEstimate = day.walkingEstimate;
  }

  if (!sections || sections.length === 0) {
    return <p className="navel-body" style={{ fontStyle: 'italic' }}>Details coming soon...</p>;
  }

  return (
    <div>
      {day.routes && day.routes.length > 0 && (
        <RouteSelector
          routes={day.routes}
          selectedId={selectedRouteId}
          onSelect={handleRouteSelect}
        />
      )}
      <div key={fadeKey} className="navel-route-content" ref={contentRef}>
        {sections.map((section, idx) => {
          switch (section.type) {
            case 'image': {
              const imgData = section.inline ? section : resolveImage(section, images);
              if (!imgData) return null;
              return (
                <div key={idx} className="navel-day-image" style={{ position: 'relative' }}>
                  <img
                    src={imgData.src}
                    alt={imgData.alt}
                    loading="lazy"
                    onError={(e) => { e.target.src = `https://placehold.co/1080x500/162422/D4A574?text=${imgData.fallback || 'Image'}`; }}
                  />
                  {/* Gradient scrim over image */}
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 100%)',
                    borderRadius: 'var(--radius-lg)', pointerEvents: 'none'
                  }} />
                </div>
              );
            }

            case 'divider':
              return <hr key={idx} className="navel-divider" />;

            case 'text':
              return (
                <div key={idx} style={{ marginBottom: 20 }}>
                  {section.title && (
                    <h4 className="navel-h3" style={{ marginBottom: 10 }}>{section.title}</h4>
                  )}
                  {section.content && renderMarkdown(section.content)}
                  {section.wet && (
                    <div className="navel-wet-tag">
                      <span style={{ fontSize: '0.875rem' }}>🌧</span>
                      <span>{section.wet}</span>
                    </div>
                  )}
                  {section.note && (
                    <p className="navel-small" style={{ marginTop: 8, fontStyle: 'italic' }}>
                      {section.note}
                    </p>
                  )}
                </div>
              );

            case 'venue':
              return (
                <NavelVenueCard
                  key={idx}
                  venue={section}
                  alternatives={alternatives}
                  city={city}
                  copyToClipboard={copyToClipboard}
                />
              );

            case 'simpleVenue':
              return (
                <div key={idx} className="navel-venue">
                  <h3 className="navel-h3" style={{ marginBottom: 6 }}>{section.heading}</h3>
                  {section.time && (
                    <span className="navel-venue-time" style={{ display: 'inline-block', marginBottom: 12 }}>{section.time}</span>
                  )}
                  {section.address && (
                    <a
                      href={section.searchQuery ? `https://www.google.com/search?q=${section.searchQuery}` : '#'}
                      target="_blank" rel="noopener noreferrer"
                      className="navel-small" style={{ display: 'block', marginBottom: 12 }}
                    >
                      {section.address}
                    </a>
                  )}
                  {section.content && renderMarkdown(section.content)}
                </div>
              );

            case 'optionBox':
              return (
                <div key={idx} className="navel-option-box">
                  <h4 className="navel-h3" style={{ marginBottom: 14 }}>{section.title}</h4>
                  {section.blocks.map((block, bIdx) => (
                    <div key={bIdx} style={{ marginBottom: bIdx < section.blocks.length - 1 ? 16 : 0 }}>
                      {block.title && (
                        <p style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4, fontFamily: 'var(--font-body)', fontSize: '0.875rem' }}>{block.title}</p>
                      )}
                      {block.content && renderMarkdown(block.content)}
                    </div>
                  ))}
                </div>
              );

            case 'infoBox':
              return (
                <div key={idx} className="navel-info-box">
                  <h3 className="navel-h3" style={{ marginBottom: 14 }}>{section.title}</h3>
                  {section.items.map((item, iIdx) => (
                    <div key={iIdx} style={{ marginBottom: 8 }}>
                      {renderMarkdown(item)}
                    </div>
                  ))}
                </div>
              );

            default:
              return null;
          }
        })}
        {walkingEstimate && (
          <p className="navel-small" style={{ marginTop: 24, paddingBottom: 8 }}>
            Estimated walking: {walkingEstimate}
          </p>
        )}
      </div>
    </div>
  );
}
